/** @format */

import {useMemo, useState} from 'react'

export type MultiSelectState<T, K extends string> = {
  selectedKeys: Set<K>
  setSelectedKeys: (keys: Iterable<K>) => void
  // get selected items, _silently_ ignore keys with no item
  getSelectedItems: () => T[]
  getItemByKey: (key: K) => T | undefined
  // will throw an error if item for key is not found
  mustGetItemByKey: (key: K) => T
}

export function useMultiSelectState<T, K extends string>({
  initialSelectedKeys = [],
  items,
  getKey,
}: {
  initialSelectedKeys?: Iterable<K>
  items: T[]
  getKey: (item: T) => K
}): MultiSelectState<T, K> {
  const [selectedKeys, setSelectedKeys] = useState(new Set(initialSelectedKeys))

  const itemsByKey = useMemo(
    () => new Map(items.map(item => [getKey(item), item])),
    [getKey, items],
  )

  return {
    selectedKeys,
    setSelectedKeys(keys) {
      setSelectedKeys(new Set(keys))
    },
    getSelectedItems() {
      const selectedItems: T[] = []
      for (const k of selectedKeys) {
        const item = itemsByKey.get(k)
        if (item) {
          selectedItems.push(item)
        }
      }
      return selectedItems
    },
    getItemByKey(key) {
      return itemsByKey.get(key)
    },
    mustGetItemByKey(key) {
      const item = itemsByKey.get(key)
      if (!item) {
        throw new Error(`Item for key=${key} does not exist.`)
      }
      return item
    },
  }
}
